// src/hooks/useQuoteActions.js
import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

export const useQuoteActions = (
    quoteId, 
    headerData, 
    rowData, 
    setRowData, 
    addonTexts,
    gridRef,
    options = {}
) => {
    const navigate = useNavigate();

    // Stavy pro modální okna
    const [isProductSearchOpen, setIsProductSearchOpen] = useState(false);
    const [isNewItemModalOpen, setIsNewItemModalOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);

    // Stavy pro Target Price Modal
    const [isTargetPriceModalOpen, setIsTargetPriceModalOpen] = useState(false);
    const [itemForTargetPriceModal, setItemForTargetPriceModal] = useState(null);
    
    const [isDuplicating, setIsDuplicating] = useState(false);

    // Stavy pro panely
    const [bulkQuantity, setBulkQuantity] = useState('');
    const [bulkOPDiscount, setBulkOPDiscount] = useState('');
    const [bulkRPDiscount, setBulkRPDiscount] = useState('');
    const [bulkCPDiscount, setBulkCPDiscount] = useState('');
    const [targetMarginInput, setTargetMarginInput] = useState('15');

    // Funkce pro Target Price Modal
    const handleOpenTargetPriceModal = useCallback((itemData) => {
        setItemForTargetPriceModal(itemData);
        setIsTargetPriceModalOpen(true);
    }, []);

    // --- PŘIDANÁ FUNKCE ---
    const handleCloseTargetPriceModal = useCallback(() => {
        setIsTargetPriceModalOpen(false);
        setItemForTargetPriceModal(null);
    }, []);
    // ----------------------

    const handleSaveTargetPrice = useCallback((rowId, newRpDiscount, newCpDiscount) => {
        setRowData(prevData =>
            prevData.map(item =>
                item.ROWID === rowId
                    ? { ...item, RP_Discount: newRpDiscount, CP_Discount: newCpDiscount }
                    : item
            )
        );
        handleCloseTargetPriceModal(); // Použijeme novou funkci pro zavření
        toast.success("Item prices and discounts updated.");
    }, [setRowData, handleCloseTargetPriceModal]); // Přidána závislost

    // Handler pro zobrazení detailu (předává se do gridu)
    const handleShowItemDetails = useCallback((itemData) => {
        setSelectedItem(itemData);
    }, []);

    // Handler pro uložení detailu (z ItemDetailPanel)
    const handleSaveItemDetail = useCallback((updatedItem) => {
        setRowData(prevData => prevData.map(item => item.ROWID === updatedItem.ROWID ? updatedItem : item));
        setSelectedItem(null);
        toast.info('Item detail updated. Save changes or push to CRM.');
    }, [setRowData]);

    // Handlery pro přidávání položek (z modálních oken)
    const handleProductSelect = useCallback((product) => {
        if (!product) return;
        const maxSequence = rowData.length > 0 ? Math.max(...rowData.map(r => r.Item_Sequence || 0)) : 0;
        const newItem = {
            ROWID: `crm_${product.id}_${Date.now()}`, CRM_Quote_ID: headerData?.CRM_Quote_ID || quoteId, Quote_Header_RowID: headerData?.ROWID || null,
            CRM_Item_ID: null, CRM_Product_Id: product.id, Item_Sequence: maxSequence + 1,
            Product_Name: product.Product_Name, Product_Code: product.Product_Code, Options_Code: product.Product_Code,
            Description: product.Description || '', Item_Notes: '', Quantity: 1, List_Price: parseFloat(product.Unit_Price) || 0,
            Weight: parseFloat(product.Weight) || 0, Delivery_Time: null, Origin: product.Origin || null, HS_Code: product.HS_Code || null,
            OP_Discount: 0, RP_Discount: 0, CP_Discount: 0, Source: 'CRM', AsCost_Unit: 0, OP_AsCosts: 0
        };
        setRowData(prevData => [...prevData, newItem]);
        setIsProductSearchOpen(false);
        toast.success(`Product "${product.Product_Name}" linked from CRM.`);
    }, [rowData, headerData, quoteId, setRowData]);

    const handleAddNewItem = useCallback((newItemData) => {
        const maxSequence = rowData.length > 0 ? Math.max(...rowData.map(r => r.Item_Sequence || 0)) : 0;
        const newItem = {
            ROWID: `newItem_${Date.now()}`, CRM_Quote_ID: headerData?.CRM_Quote_ID || quoteId, Quote_Header_RowID: headerData?.ROWID || null,
            CRM_Item_ID: null, CRM_Product_Id: null, Item_Sequence: maxSequence + 1,
            Product_Name: newItemData.Product_Name, Product_Code: newItemData.Product_Code, Options_Code: newItemData.Product_Code,
            Description: newItemData.Description, Item_Notes: newItemData.Item_Notes, Quantity: parseFloat(newItemData.Quantity) || 1,
            List_Price: parseFloat(newItemData.List_Price) || 0, Weight: parseFloat(newItemData.Weight) || 0, Delivery_Time: parseInt(newItemData.Delivery_Time, 10) || null,
            Origin: newItemData.Origin, HS_Code: newItemData.HS_Code, OP_Discount: 0, RP_Discount: 0, CP_Discount: 0, Source: 'Manual',
            AsCost_Unit: 0, OP_AsCosts: 0
        };
        setRowData(prevData => [...prevData, newItem]);
        toast.success("New item added. Don't forget to save changes.");
    }, [rowData, headerData, quoteId, setRowData]);

    // Handlery pro akce v gridu a panelech
    const createBulkUpdateHandler = useCallback((field, value, setValue) => {
        return () => {
          const api = gridRef.current.api;
          const selectedNodes = api.getSelectedNodes();
          if (selectedNodes.length === 0) { toast.warn("Please select rows to update first."); return; }
          const numericValue = parseFloat(value);
          if (isNaN(numericValue)) { toast.error("Please enter a valid number."); return; }
          const updatedRowData = rowData.map(row => {
            const isSelected = selectedNodes.some(node => node.data.ROWID === row.ROWID);
            if (isSelected) { return { ...row, [field]: numericValue }; }
            return row;
          });
          setRowData(updatedRowData);
          setValue('');
          api.deselectAll();
          toast.info(`${selectedNodes.length} items have been updated.`);
        };
    }, [gridRef, rowData, setRowData]);

    const handleBulkUpdateQuantity = createBulkUpdateHandler('Quantity', bulkQuantity, setBulkQuantity);
    const handleBulkUpdateOPDiscount = createBulkUpdateHandler('OP_Discount', bulkOPDiscount, setBulkOPDiscount);
    const handleBulkUpdateRPDiscount = createBulkUpdateHandler('RP_Discount', bulkRPDiscount, setBulkRPDiscount);
    const handleBulkUpdateCPDiscount = createBulkUpdateHandler('CP_Discount', bulkCPDiscount, setBulkCPDiscount);

    const handleSetTargetMargin = useCallback(() => {
        const api = gridRef.current.api;
        const selected = api.getSelectedRows();
        if (!selected.length) { toast.warn('Select rows first'); return; }
        const pct = parseFloat(targetMarginInput);
        if (isNaN(pct)) { toast.error('Invalid margin'); return; }
        setRowData(rs => rs.map(r => {
            if (!selected.some(sel => sel.ROWID === r.ROWID)) return r;
            const listPrice = parseFloat(r.List_Price) || 0;
            const opDiscountPercent = parseFloat(r.OP_Discount) || 0;
            const asCostUnit = parseFloat(r.AsCost_Unit) || 0;
            const opPrice = listPrice * (1 - opDiscountPercent / 100);
            const finalOpUnitPrice = opPrice + asCostUnit;
            if (finalOpUnitPrice < 0) return r;
            const rpPrice = listPrice * (1 - (parseFloat(r.RP_Discount) || 0) / 100);
            const targetCpPrice = finalOpUnitPrice * (1 + pct / 100);
            let newCpDiscountPercent = 0;
            if (rpPrice > 0 && rpPrice > targetCpPrice) newCpDiscountPercent = (1 - (targetCpPrice / rpPrice)) * 100;
            return { ...r, CP_Discount: parseFloat(newCpDiscountPercent.toFixed(2)) };
        }));
        api.deselectAll();
    }, [targetMarginInput, setRowData, gridRef]);

    const handleDeleteSelectedRows = useCallback(() => {
        const api = gridRef.current.api;
        const selected = api.getSelectedRows();
        if (!selected.length) return;
        if (!window.confirm(`Really delete ${selected.length} row(s)?`)) return;
        setRowData(prev => prev.filter(r => !selected.some(sel => sel.ROWID === r.ROWID)));
        api.deselectAll();
    }, [setRowData, gridRef]);

    const handleRenumberSequence = useCallback(() => {
        if (!gridRef.current) return;
        const api = gridRef.current.api;
        const reorderedNodes = [];
        api.forEachNodeAfterFilterAndSort(node => {
            reorderedNodes.push(node.data);
        });
        const newRowData = reorderedNodes.map((data, index) => ({ ...data, Item_Sequence: index + 1 }));
        setRowData(newRowData);
        toast.success("Sequence re-numbered.");
    }, [setRowData, gridRef]);

    const handleDuplicate = useCallback(async (duplicationType) => {
        if (!headerData || !rowData || !addonTexts) {
            toast.error("Cannot duplicate, data is not fully loaded.");
            return;
        }
        // Warn if there are unsaved local or CRM-unsynced changes
        const { hasLocalChanges = false } = options;
        const crmDirty = headerData && headerData.Items_Synced === false;
        if (hasLocalChanges || crmDirty) {
            let reason = '';
            if (hasLocalChanges && crmDirty) {
                reason = 'unsaved local changes and changes not synced to CRM';
            } else if (hasLocalChanges) {
                reason = 'unsaved local changes';
            } else {
                reason = 'changes not synced to CRM';
            }
            const proceed = window.confirm(`You have ${reason}. Do you want to duplicate anyway?`);
            if (!proceed) { return; }
        }
        setIsDuplicating(true);
        const toastId = toast.loading(`Creating new ${duplicationType}...`);
        try {
            const sourcePayload = { headerData, rowData, addonTexts };
            const response = await fetch('/server/quote_service/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'duplicate_quote',
                    sourcePayload,
                    duplicationType
                })
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.details || errorData.error || "Unknown server error.");
            }
            const result = await response.json();
            toast.update(toastId, {
                render: "Quote duplicated successfully!",
                type: "success",
                isLoading: false,
                autoClose: 3000
            });
            if (window.confirm(`New quote "${result.newQuoteName}" has been created. Do you want to open it now?`)) {
                navigate(`/quote/${result.newQuoteId}`);
            }
        } catch (err) {
            console.error("Duplication Error:", err);
            toast.update(toastId, {
                render: `Duplication Failed: ${err.message}`,
                type: "error",
                isLoading: false,
                autoClose: 5000
            });
        } finally {
            setIsDuplicating(false);
        }
    }, [headerData, rowData, addonTexts, navigate, options]);

    // Vrátíme všechny stavy a funkce, které bude QuoteEditor a jeho potomci potřebovat
    return {
        isProductSearchOpen, setIsProductSearchOpen,
        isNewItemModalOpen, setIsNewItemModalOpen,
        selectedItem, setSelectedItem,
        
        isTargetPriceModalOpen,
        itemForTargetPriceModal,
        handleOpenTargetPriceModal,
        handleSaveTargetPrice,
        handleCloseTargetPriceModal, // --- PŘIDANÝ EXPORT ---

        bulkQuantity, setBulkQuantity,
        bulkOPDiscount, setBulkOPDiscount,
        bulkRPDiscount, setBulkRPDiscount,
        bulkCPDiscount, setBulkCPDiscount,
        targetMarginInput, setTargetMarginInput,
        handleShowItemDetails,
        handleSaveItemDetail,
        handleProductSelect,
        handleAddNewItem,
        handleBulkUpdateQuantity,
        handleBulkUpdateOPDiscount,
        handleBulkUpdateRPDiscount,
        handleBulkUpdateCPDiscount,
        handleSetTargetMargin,
        handleDeleteSelectedRows,
        handleRenumberSequence,
        handleDuplicate,
        isDuplicating
    };
};